import { useStoryblokApi } from "@storyblok/vue";
import { fetchStoryblokContentByFolder } from "~/utils/storyblok/fetchStoryblokContentByFolder";
import { useStoryblokSiteFolder } from "~/composables/useStoryblokSiteFolder";

export interface StoryblokFetchOptions {
	version: string;
	language: string;
	resolve_links?: "url" | "story";
	resolve_relations?: string;
}

export interface StoryblokFetcher {
	/**
	 * Fetch a Storyblok story by its slug, routed through the active site
	 * folder chain (own folder → default site folder). Whether the chain
	 * also falls back to the shared space root is governed by the active
	 * locale's `rootFallback` flag (see `useStoryblokSiteFolder().strict`).
	 *
	 * Use this for any "layout-tier" story whose slug is fixed in code
	 * (e.g. `layout`, `category-page-layout`, `product-page-layout`,
	 * `blog-layout`, `find-store-layout`). For URL-driven slugs the page
	 * code typically calls fetchStoryblokContentByFolder directly with
	 * extra error handling around it.
	 *
	 * Pass an array of paths to support legacy/migration shapes:
	 *   - `paths[0]` is the primary slug — tried across the full folder
	 *     chain (`<folder>/path → <fallback>/path → path`).
	 *   - `paths[1…]` are legacy root-only fallbacks — tried at the space
	 *     root after the primary chain exhausts, with no folder prefix.
	 *
	 * Example: `fetchStory(["layouts/main", "layouts"], …)` resolves in
	 * this order on a multi-site deploy:
	 *   site-uk/layouts/main → site-main/layouts/main → layouts/main → layouts
	 *
	 * Return type mirrors `storyblokApi.get(...)` for drop-in replacement.
	 */
	fetchStory: (
		path: string | string[],
		options: StoryblokFetchOptions
	) => Promise<any>;
}

export const useStoryblokFetcher = (): StoryblokFetcher => {
	const api = useStoryblokApi();
	const { folder, fallbackFolder, strict } = useStoryblokSiteFolder();

	const fetchStory: StoryblokFetcher["fetchStory"] = (path, options) =>
		fetchStoryblokContentByFolder(api, path, options, {
			folders: [folder.value, fallbackFolder.value],
			strict: strict.value
		});

	return { fetchStory };
};
